import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { WorkflowService } from "../workflow.service";
import { 
    workflowItemsLoad, 
    workflowItemsLoadedFromApi, 
    workflowItemsLoadedFromApiFailed,
    workflowGroupsLoad, 
    workflowGroupsLoadedFromApi, 
    workflowGroupsLoadedFromApiFailed, 
    workflowItemLoad,
    workflowItemLoadedFromApi,
    workflowItemLoadedFromApiFailed
} from "../reducers/workflow.reducer";
import { catchError, map, mergeMap } from "rxjs/operators";
import { of } from "rxjs";

@Injectable()
/**
 * ngrx side effects for the workflow store. Loads workflow items (per user), workflow
 * groups and single workflow items from the Loans API via WorkflowService, mapping
 * success and failure onto the workflow reducer actions.
 */
export class WorkflowEffects {
    constructor(
        private actions$: Actions,
        private workflowService: WorkflowService
    ) {}

    /** Effect: loads the current user's workflow items and dispatches the loaded/failed action. */
    loadWorkflowItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(workflowItemsLoad),
            mergeMap(() =>
                this.workflowService.getWorkflowItemsByUsername().pipe(
                    map(workflowItems => workflowItemsLoadedFromApi({ workflowItems })),
                    catchError(() => of(workflowItemsLoadedFromApiFailed))
                )
            )
        )
    );

    /** Effect: loads all workflow groups and dispatches the loaded/failed action. */
    loadWorkflowGroups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(workflowGroupsLoad),
            mergeMap(() =>
                this.workflowService.getWorkflowGroups().pipe(
                    map(workflowGroups => workflowGroupsLoadedFromApi({ workflowGroups })),
                    catchError(() => of(workflowGroupsLoadedFromApiFailed()))
                )
            )
        )
    );

    /** Effect: loads a single workflow item by id; fails immediately when no id is supplied. */
    loadWorkflowItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(workflowItemLoad),
            mergeMap(({ id }) => {
                if (!id) return of(workflowItemLoadedFromApiFailed());
                return this.workflowService.getWorkflowItem(id).pipe(
                    map(workflowItem => workflowItemLoadedFromApi({ workflowItem })),
                    catchError(() => of(workflowItemLoadedFromApiFailed()))
                );
            })
        )
    );
}